import React, { useState, useEffect } from 'react';
import { Modal, Form, Checkbox, Row, Col, Divider, message } from 'antd';
import {MODULES, MODULE_LABELS } from '../../models/permissions';
import { updateUserPermissions } from '../../services/userService';
import { logActivity } from '../../services/auditService';
import { ACTIVITY_TYPES } from '../../models/activityTypes';
import { useAuth } from '../../context/AuthContext';

const PermissionsModal = ({ visible, onClose, user, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = useState({});
  const { user: currentUser } = useAuth();

  useEffect(() => {
    if (user && visible) {
      const userPermissions = user.permissions || {};
      setPermissions(userPermissions);
      form.setFieldsValue(userPermissions);
    }
  }, [user, visible, form]);

  const handlePermissionChange = (module, checked) => {
    setPermissions(prev => ({
      ...prev,
      [module]: checked
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const result = await updateUserPermissions(user.id, permissions);
      
      if (result.success) {
        // Log the activity
        if (currentUser) {
          const oldPermissions = user.permissions || {};
          const changedPermissions = {};
          
          // Identifier les permissions qui ont changé
          Object.keys(permissions).forEach(module => {
            if (permissions[module] !== oldPermissions[module]) {
              changedPermissions[module] = { 
                old: oldPermissions[module] || false, 
                new: permissions[module] 
              };
            }
          });
          
          await logActivity(
            ACTIVITY_TYPES.USER_UPDATED,
            currentUser.id,
            'user',
            user.id,
            { permissions: changedPermissions },
            `Permissions mises à jour pour ${user.firstName} ${user.lastName}`
          );
        }
        
        message.success('Permissions mises à jour avec succès');
        onSuccess?.(permissions);
        onClose();
      } else {
        message.error('Erreur lors de la mise à jour des permissions');
      }
    } catch (error) {
      message.error('Une erreur est survenue');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={`Gérer les permissions - ${user?.firstName} ${user?.lastName}`}
      open={visible}
      onOk={handleSubmit}
      onCancel={onClose}
      okText="Enregistrer"
      cancelText="Annuler"
      confirmLoading={loading}
      width={600}
    >
      <Form form={form} layout="vertical">
        <h3>Sélectionnez les modules accessibles:</h3>
        <Divider />
        
        <Row gutter={[16, 16]}>
          {Object.entries(MODULES).map(([key, module]) => (
            <Col span={24} key={module}>
              <Checkbox
                checked={permissions[module] || false}
                onChange={(e) => handlePermissionChange(module, e.target.checked)}
              >
                <span style={{ fontSize: '14px', marginLeft: '8px' }}>
                  {MODULE_LABELS[module]}
                </span>
              </Checkbox>
              <span style={{ color: '#999', marginLeft: '28px', fontSize: '12px' }}>
                {module === MODULES.SUBSCRIBERS && 'Gestion des abonnés'}
                {module === MODULES.METERS && 'Gestion des compteurs'}
                {module === MODULES.READINGS && 'Enregistrement des relevés'}
                {module === MODULES.BILLING && 'Génération de factures'}
                {module === MODULES.PAYMENTS && 'Enregistrement des paiements'}
                {module === MODULES.REPORTS && 'Consultation des rapports'}
              </span>
            </Col>
          ))}
        </Row>
      </Form>
    </Modal>
  );
};

export default PermissionsModal;
